/**
 * Time parsing patterns and utilities for SmartTimeInput
 * Handles various time formats and natural language expressions
 */

export interface TimePattern {
  pattern: RegExp
  parser: (match: RegExpMatchArray) => { hour: number; minute: number } | null
  confidence: number
  description: string
}

/**
 * Common time format patterns with their parsers
 */
export const TIME_PATTERNS: TimePattern[] = [
  // 12-hour format with colon: "2:30 PM", "2:30pm", "2:30 P"
  {
    pattern: /^(\d{1,2}):(\d{2})\s*(pm|p\.m\.|p|am|a\.m\.|a)$/i,
    parser: (match) => {
      const hour = parseInt(match[1], 10)
      const minute = parseInt(match[2], 10)
      const period = match[3].toLowerCase()

      if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null

      const isPM = period.startsWith('p')
      const adjustedHour =
        hour === 12 ? (isPM ? 12 : 0) : isPM ? hour + 12 : hour

      return { hour: adjustedHour, minute }
    },
    confidence: 1.0,
    description: '12-hour format with colon and meridiem',
  },

  // 12-hour format without colon: "230 PM", "230pm"
  {
    pattern: /^(\d{3,4})\s*(pm|p\.m\.|p|am|a\.m\.|a)$/i,
    parser: (match) => {
      const digits = match[1]
      const period = match[2].toLowerCase()

      let hour: number, minute: number
      if (digits.length === 3) {
        hour = parseInt(digits[0], 10)
        minute = parseInt(digits.slice(1), 10)
      } else {
        hour = parseInt(digits.slice(0, 2), 10)
        minute = parseInt(digits.slice(2), 10)
      }

      if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null

      const isPM = period.startsWith('p')
      const adjustedHour =
        hour === 12 ? (isPM ? 12 : 0) : isPM ? hour + 12 : hour

      return { hour: adjustedHour, minute }
    },
    confidence: 0.9,
    description: '12-hour format without colon',
  },

  // 24-hour format with colon: "14:30", "09:15"
  {
    pattern: /^(\d{1,2}):(\d{2})$/,
    parser: (match) => {
      const hour = parseInt(match[1], 10)
      const minute = parseInt(match[2], 10)

      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null

      return { hour, minute }
    },
    confidence: 1.0,
    description: '24-hour format with colon',
  },

  // 24-hour format without colon: "1430", "0915"
  {
    pattern: /^(\d{4})$/,
    parser: (match) => {
      const digits = match[1]
      const hour = parseInt(digits.slice(0, 2), 10)
      const minute = parseInt(digits.slice(2), 10)

      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null

      return { hour, minute }
    },
    confidence: 0.8,
    description: '24-hour format without colon',
  },

  // Hour only with meridiem: "2pm", "2 PM", "2p"
  {
    pattern: /^(\d{1,2})\s*(pm|p\.m\.|p|am|a\.m\.|a)$/i,
    parser: (match) => {
      const hour = parseInt(match[1], 10)
      const period = match[2].toLowerCase()

      if (hour < 1 || hour > 12) return null

      const isPM = period.startsWith('p')
      const adjustedHour =
        hour === 12 ? (isPM ? 12 : 0) : isPM ? hour + 12 : hour

      return { hour: adjustedHour, minute: 0 }
    },
    confidence: 0.9,
    description: 'Hour only with meridiem',
  },

  // Hour only (ambiguous): "14", "9"
  {
    pattern: /^(\d{1,2})$/,
    parser: (match) => {
      const hour = parseInt(match[1], 10)

      // For 24-hour format
      if (hour >= 0 && hour <= 23) {
        return { hour, minute: 0 }
      }

      return null
    },
    confidence: 0.4, // Low confidence due to ambiguity
    description: 'Hour only (ambiguous format)',
  },

  // Partial input with colon: "2:3" -> "2:03"
  {
    pattern: /^(\d{1,2}):(\d{1})$/,
    parser: (match) => {
      const hour = parseInt(match[1], 10)
      const minute = parseInt('0' + match[2], 10) // "3" becomes "03", "6" becomes "06"

      // Assume 12-hour format for single digit hours, 24-hour for double
      if (hour >= 1 && hour <= 12 && minute <= 59) {
        return { hour, minute }
      } else if (hour >= 0 && hour <= 23 && minute <= 59) {
        return { hour, minute }
      }

      return null
    },
    confidence: 0.7,
    description: 'Partial minute input with colon',
  },

  // Decimal hours: "2.5" -> "2:30"
  {
    pattern: /^(\d{1,2})\.(\d{1,2})$/,
    parser: (match) => {
      const hour = parseInt(match[1], 10)
      const decimal = parseInt(match[2], 10)

      // Convert decimal to minutes (0.5 = 30 minutes, 0.25 = 15 minutes)
      const minute = Math.round((decimal / Math.pow(10, match[2].length)) * 60)

      if (hour >= 0 && hour <= 23 && minute <= 59) {
        return { hour, minute }
      }

      return null
    },
    confidence: 0.6,
    description: 'Decimal hour format',
  },

  // Fractional expressions like "quarter past 3" or "half to 4"
  {
    pattern:
      /^(quarter|half)\s+(past|after|to|before)\s+(\d{1,2})(?:\s*(am|a\.m\.|a|pm|p\.m\.|p))?$/i,
    parser: (match) => {
      const quantity = match[1].toLowerCase()
      const direction = match[2].toLowerCase()
      let hour12 = parseInt(match[3], 10)

      if (Number.isNaN(hour12) || hour12 < 1 || hour12 > 12) {
        return null
      }

      const meridiem = (match[4] || '').toLowerCase()
      const delta = quantity === 'half' ? 30 : 15
      const isSubtract = direction === 'to' || direction === 'before'

      if (isSubtract) {
        hour12 = hour12 === 1 ? 12 : hour12 - 1
      }

      let hour = hour12 % 12

      if (meridiem) {
        if (meridiem.startsWith('p')) {
          hour += 12
        } else if (meridiem.startsWith('a') && hour === 12) {
          hour = 0
        }
      }

      const minute = isSubtract ? 60 - delta : delta

      if (!isValidTime(hour, minute)) {
        return null
      }

      return { hour, minute }
    },
    confidence: 0.8,
    description: 'Fraction phrases like "quarter past 3"',
  },
]

/**
 * Named time expressions (case-insensitive)
 */
export const NAMED_TIMES: Record<
  string,
  { hour: number; minute: number; confidence: number }
> = {
  midnight: { hour: 0, minute: 0, confidence: 1.0 },
  noon: { hour: 12, minute: 0, confidence: 1.0 },
  morning: { hour: 9, minute: 0, confidence: 0.7 },
  afternoon: { hour: 14, minute: 0, confidence: 0.7 },
  evening: { hour: 18, minute: 0, confidence: 0.7 },
  night: { hour: 21, minute: 0, confidence: 0.7 },
}

export const NAMED_TIME_KEYS = Object.keys(NAMED_TIMES)

/**
 * Common fractional expressions
 */
export const FRACTIONAL_TIMES: Record<string, number> = {
  'quarter past': 15,
  'quarter after': 15,
  'half past': 30,
  'quarter to': -15,
  'quarter before': -15,
}

/**
 * Normalizes input text for parsing
 */
export function normalizeInput(input: string): string {
  return (
    input
      .trim()
      .toLowerCase()
      // Normalize separators
      .replace(/：/g, ':') // Replace full-width colon
      .replace(/\s+/g, ' ') // Normalize whitespace
      // Clean up common typos
      .replace(/pm/g, 'pm')
      .replace(/am/g, 'am')
      .replace(/p\.m\./g, 'pm')
      .replace(/a\.m\./g, 'am')
      // Remove duplicate characters
      .replace(/::+/g, ':')
      .replace(/\s{2,}/g, ' ')
  )
}

/**
 * Validates a time object
 */
export function isValidTime(hour: number, minute: number): boolean {
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59
}

/**
 * Rounds minutes to the nearest step interval
 * Returns 0-59 (caps at 59 to prevent invalid minute values)
 */
export function roundToStep(minute: number, step: number = 15): number {
  const rounded = Math.round(minute / step) * step
  return Math.min(rounded, 59)
}

/**
 * Applies business hours bias for ambiguous times
 * Prefers times during typical business hours (9 AM - 5 PM by default)
 */
export function applyBusinessHoursBias(
  hour: number,
  minute: number,
  businessHourStart: number = 9,
  businessHourEnd: number = 17,
): { hour: number; minute: number; confidence: number } {
  // If already in 24-hour format, no bias needed
  if (hour > 12) {
    return { hour, minute, confidence: 1.0 }
  }

  // For ambiguous hours (1-12), prefer PM during business hours
  const amVersion = hour === 12 ? 0 : hour
  const pmVersion = hour === 12 ? 12 : hour + 12

  // Check if PM version falls within business hours
  if (pmVersion >= businessHourStart && pmVersion <= businessHourEnd) {
    return { hour: pmVersion, minute, confidence: 0.8 }
  }

  // Otherwise prefer AM
  return { hour: amVersion, minute, confidence: 0.6 }
}
